import type { SeatInfo } from "@app/protocol";
import type { hearts } from "@app/rules";
import { SUIT_GLYPH } from "../components/Card";
import type { ToastData } from "../components/Toast";
import type { T } from "./tableText";
import { signed } from "./toastParts";

type Seats = readonly (SeatInfo | null)[];
type Event = {
  code: string;
  seat?: number | undefined;
  data?: Record<string, string | number> | undefined;
};

const RANK_TEXT: Readonly<Record<number, string>> = { 11: "J", 12: "Q", 13: "K", 14: "A" };

/** "♠Q ♥10 ♦3": short enough for a toast. */
function cardsText(cards: readonly { suit: keyof typeof SUIT_GLYPH; rank: number }[]): string {
  return cards
    .map((card) => `${SUIT_GLYPH[card.suit]}${RANK_TEXT[card.rank] ?? card.rank}`)
    .join(" ");
}

/** A Hearts seat's pill: choosing or passed before the hand, then the points it has taken. */
export function heartsSeatSub(
  view: hearts.HeartsView,
  seat: number,
  yourSeat: number | null,
  t: T,
): string {
  if (view.phase !== "passing")
    return t("hearts.seat.points", { points: view.handPoints[seat] ?? 0 });
  if (!view.pass.pending.includes(seat)) return t("hearts.seat.passed");
  return seat === yourSeat ? t("hearts.seat.choose") : t("hearts.seat.choosing");
}

/** Who handed you your three cards: the seat whose target you are. */
function giverOf(view: hearts.HeartsView): number | null {
  const steps = { left: 1, right: 3, across: 2, hold: null }[view.pass.direction];
  return steps === null ? null : (view.yourSeat + steps) % 4;
}

/** The pass has happened: what you were handed and by whom; a watcher only hears the direction. */
function passedText(view: hearts.HeartsView, seats: Seats, t: T): string {
  const { direction, received } = view.pass;
  const giver = giverOf(view);
  if (received !== null && giver !== null)
    return t("hearts.event.received", {
      name: seats[giver]?.name ?? "?",
      cards: cardsText(received),
    });
  return direction === "hold" ? "" : t(`hearts.event.passed.${direction}`);
}

/** Hearts events worth a sentence. */
export function heartsEventText(
  last: Event,
  view: hearts.HeartsView,
  seats: Seats,
  t: T,
): string | null {
  if (last.code === "cards-passed") return passedText(view, seats, t);
  if (last.code === "hearts-broken") return t("hearts.event.broken");
  if (last.code === "queen-taken")
    return t("hearts.event.queen", { name: seats[last.seat ?? 0]?.name ?? "?" });
  if (last.code === "hand-dealt" && last.data?.direction === "hold")
    return t("hearts.event.hold", { hand: view.hand });
  return null;
}

function moonToast(seats: Seats, yourSeat: number | null, moon: number, line: string, t: T) {
  return {
    title: t("hearts.handEnd.moon", { name: seats[moon]?.name ?? "?" }),
    sub: `${t("hearts.handEnd.moon.sub")} · ${line}`,
    tone: moon === yourSeat ? ("success" as const) : ("error" as const),
    member: seats[moon] ?? null,
  };
}

/** Between hands: the moon when it was shot, else what you took; every seat's score underneath. */
export function heartsHandEnd(
  view: hearts.HeartsView,
  seats: Seats,
  yourSeat: number | null,
  t: T,
): ToastData | null {
  const result = view.lastHandResult;
  if (result === null) return null;
  const line = result.deltas
    .map((delta, seat) => `${seats[seat]?.name ?? "?"} ${signed(delta)}`)
    .join(" · ");
  if (result.moon !== null) return moonToast(seats, yourSeat, result.moon, line, t);
  // A watcher looks on from seat 0: the hand is scored, but none of it is theirs.
  if (yourSeat === null)
    return { title: t("hearts.handEnd.watch", { hand: result.hand }), sub: line, tone: "neutral" };
  const mine = result.deltas[yourSeat] ?? 0;
  return {
    title: t("hearts.handEnd.title", { hand: result.hand, points: mine }),
    sub: line,
    tone: mine <= 0 ? "success" : "neutral",
  };
}
